import { SearchResultData } from "../../app/slices/SearchResultsSlice";
import { LocalizedStringRecord } from "../../app/slices/TrainingLocalizationSlice";
import { InteractionFactoryEntity } from "../../features/interaction_factory/InteractionFactory";
import PropLabelQuizPreview from "../../features/previews/prop_label_quiz/PropLabelQuizPreview";
import { ValidationHelper } from "../../features/validation/ValidationHelper";
import { GetNewGuid } from "../../helpers/GuidHelper";
import { Actor } from "../Actor";
import { Prop } from "../Prop";
import Take from "../Take";
import Interaction, { InteractionType } from "./Interaction";
import {
  compactSearchableStrings,
  SearchLookupContext,
} from "./SearchableInteractionValues";

export enum CreatorPropLabelPreferredSide {
  Auto = "Auto",
  TopLeft = "TopLeft",
  Top = "Top",
  TopRight = "TopRight",
  Left = "Left",
  Right = "Right",
  BottomLeft = "BottomLeft",
  Bottom = "Bottom",
  BottomRight = "BottomRight",
}

export type PropLabelQuizPreferredSide = `${CreatorPropLabelPreferredSide}`;

const defaultDistanceBias = 1;
const minDistanceBias = 0.5;
const maxDistanceBias = 3;

const isPropLabelPreferredSide = (
  value: unknown,
): value is PropLabelQuizPreferredSide =>
  Object.values(CreatorPropLabelPreferredSide).includes(
    value as CreatorPropLabelPreferredSide,
  );

const clampDistanceBias = (value: number) =>
  Math.min(Math.max(value, minDistanceBias), maxDistanceBias);

const getDistanceBias = (value: unknown) =>
  typeof value === "number" && Number.isFinite(value)
    ? clampDistanceBias(value)
    : defaultDistanceBias;

export interface IPropLabelQuizAnswer {
  id: string;
  correct: boolean;
  text: string;
  propGuid: string;
  tag: string;
  inspectDefinitionId?: string;
  preferredSide: PropLabelQuizPreferredSide;
  distanceBias: number;
}

export interface IPropLabelQuiz extends Interaction {
  title: string;
  answers: IPropLabelQuizAnswer[];
}

const createAnswer = (
  text: string,
  correct: boolean,
  preferredSide: PropLabelQuizPreferredSide,
): IPropLabelQuizAnswer => ({
  id: GetNewGuid(),
  correct,
  text,
  propGuid: "",
  tag: "",
  preferredSide,
  distanceBias: defaultDistanceBias,
});

const migrateAnswer = (
  answer: Partial<IPropLabelQuizAnswer>,
  fallbackText: string,
  index: number,
): IPropLabelQuizAnswer => ({
  id: answer.id ?? GetNewGuid(),
  correct: answer.correct ?? index === 0,
  text: answer.text ?? fallbackText,
  propGuid: answer.propGuid ?? "",
  tag: answer.tag ?? "",
  inspectDefinitionId:
    typeof answer.inspectDefinitionId === "string" &&
    answer.inspectDefinitionId.length > 0
      ? answer.inspectDefinitionId
      : undefined,
  preferredSide: isPropLabelPreferredSide(answer.preferredSide)
    ? answer.preferredSide
    : CreatorPropLabelPreferredSide.Auto,
  distanceBias: getDistanceBias(answer.distanceBias),
});

export const propLabelQuizInteraction = (): InteractionFactoryEntity => {
  const view = <PropLabelQuizPreview />;
  const filterType = "Prop Label Quiz";

  const create = (getKeys: (amount: number) => string[]): IPropLabelQuiz => {
    const keys = getKeys(3);

    return {
      id: GetNewGuid(),
      name: "Prop Label Quiz",
      type: InteractionType.PropLabelQuiz,
      title: keys[0],
      answers: [
        createAnswer(keys[1], true, CreatorPropLabelPreferredSide.Auto),
        createAnswer(keys[2], false, CreatorPropLabelPreferredSide.Auto),
      ],
      properties: [],
    };
  };

  const migrate = (
    from: number,
    to: number,
    interaction: Interaction,
  ): IPropLabelQuiz => {
    const casted = interaction as IPropLabelQuiz;
    const answers =
      casted.answers?.map((answer, index) =>
        migrateAnswer(answer, answer.text ?? "", index),
      ) ?? [];

    return {
      ...casted,
      type: InteractionType.PropLabelQuiz,
      answers,
      properties: casted.properties ?? [],
    };
  };

  const copy = (
    interaction: Interaction,
    getKeys: (amount: number) => string[],
    replaceKey: (from: string, to: string) => void,
  ): IPropLabelQuiz => {
    const quiz = interaction as IPropLabelQuiz;
    const keys = getKeys(quiz.answers.length + 1);
    const title = keys[0];
    replaceKey(quiz.title, title);

    return {
      id: GetNewGuid(),
      name: quiz.name,
      type: InteractionType.PropLabelQuiz,
      title,
      answers: quiz.answers.map((answer, index) => {
        const text = keys[index + 1];
        replaceKey(answer.text, text);
        return {
          ...answer,
          id: GetNewGuid(),
          text,
        };
      }),
      properties: quiz.properties?.map((property) => ({ ...property })) ?? [],
    };
  };

  const validate = (
    interaction: Interaction,
    getValue: (key: string) => string,
    take: Take,
    actorPresets: Actor[],
  ): string[] => {
    const quiz = interaction as IPropLabelQuiz;
    const warnings: string[] = [];

    ValidationHelper.ValidateKey(quiz.title, getValue, warnings, "Title");
    quiz.answers.forEach((answer, index) => {
      ValidationHelper.ValidateKey(
        answer.text,
        getValue,
        warnings,
        `Answer ${index + 1}`,
      );
    });

    return warnings;
  };

  const filter = (
    interaction: Interaction,
    takeName: string,
    getMatchingResults: (
      searchableStrings: string[],
      takeName: string,
      interaction: Interaction,
      filterType: string,
    ) => SearchResultData[],
    getValue: (key: string) => string,
    searchLookupContext?: SearchLookupContext,
  ): SearchResultData[] => {
    const quiz = interaction as IPropLabelQuiz;
    const searchableStrings = compactSearchableStrings(
      quiz.name,
      quiz.title,
      ...quiz.answers.flatMap((answer) => [
        answer.text,
        answer.tag,
        answer.preferredSide,
        answer.distanceBias,
        ...(searchLookupContext?.getPropSearchTerms(answer.propGuid) ?? [
          answer.propGuid,
        ]),
      ]),
    );

    return getMatchingResults(
      searchableStrings,
      takeName,
      interaction,
      filterType,
    );
  };

  const validateErrors = (
    interaction: Interaction,
    parentTake: Take,
    currentTake: Take,
    actorPresets: Actor[],
    allPropPresets: Prop[],
    localizedStringRecords: LocalizedStringRecord,
  ): string[] => {
    const quiz = interaction as IPropLabelQuiz;
    const errors: string[] = [];

    if (quiz.answers.length === 0) {
      errors.push("Quiz must have at least one answer");
    }

    if (!quiz.answers.some((answer) => answer.correct)) {
      errors.push("Please add a correct answer");
    }

    quiz.answers.forEach((answer, index) => {
      if (!answer.propGuid) {
        errors.push(`Answer ${index + 1} requires a prop`);
      }
      if (!answer.tag) {
        errors.push(`Answer ${index + 1} requires a clickable tag`);
      }
    });

    return errors;
  };

  return {
    View: view,
    FilterType: filterType,
    Create: create,
    Migrate: migrate,
    Copy: copy,
    Validate: validate,
    Filter: filter,
    ValidateErrors: validateErrors,
  };
};
